import TagManager from 'react-gtm-module'
import toast from 'react-hot-toast'
// import { isExpired } from 'react-jwt'
import { loadStripe } from '@stripe/stripe-js'
import { captureEvent } from '@sentry/react'

import _ from 'lodash'
import {
  MAX_IDENTITY_VERIFICATION_ATTEMPTS,
  UserStatus,
  VerificationStatus,
} from '@playback-rewards/shared-libraries'
import { axiosBackendInstance } from '../axiosInstance'
import {
  ANALYTICS_EVENT,
  ANALYTICS_FRONTEND_EVENT,
  CHECK_BUNDLE_TIME,
  ERROR_REGEX,
  ERROR_TEXT,
  ERRORS,
  INSTALL_VALIDATING_MINUTES,
  LOCAL_STORAGE,
  MILLISECONDS_IN_MINUTE,
  REACT_NATIVE_STATUS,
  TIMER_EXPIRED,
  USER_PLATFORM,
  VIRTUAL_CURRENCY_TYPE,
} from '../constants'
import { generateAuthLinkPost } from '../services/authService'
import MovingImage from '../components/movingImage'
import { sendEventAnalytic } from '../services/userService'
import { createVerificationSession } from '../services/stripeService'
import { createVerisoulSession } from '../services/verisoulService'
import { useRefreshToken } from '../hooks/useRefreshToken'
import { errorHandler } from './errorHandler'
import { getAnimationIconsCap } from './animations'

export const formatNumber = (number) => {
  return parseFloat(number).toLocaleString('en-US')
}

export const formatPositiveNumber = (number) => {
  return number > 0 ? formatNumber(number) : null
}

export const convertTo_K_M_B_Format = (num) => {
  if (num >= 1000000000) {
    return Math.floor(num / 10000000) / 100 + 'B'
  } else if (num >= 1000000) {
    return Math.floor(num / 10000) / 100 + 'M'
  } else if (num >= 10000) {
    return Math.floor(num / 10) / 100 + 'K'
  }
  return formatNumber(num)
}

/**
 * Converts formatted K, M, B values back to a number.
 */
export const parseFormattedNumber = (formattedStr) => {
  if (formattedStr.endsWith('B')) {
    return parseFloat(formattedStr) * 1000000000
  } else if (formattedStr.endsWith('M')) {
    return parseFloat(formattedStr) * 1000000
  } else if (formattedStr.endsWith('K')) {
    return parseFloat(formattedStr) * 1000
  }
  return parseFloat(formattedStr.replace(/,/g, '')) || 0
}

export const isUserVerified = (user) => {
  return user?.identityVerification?.status === VerificationStatus.VERIFIED
}

export const isUserFrozen = (user) => {
  return user?.userStatus === UserStatus.FROZEN
}

export const isUserOutOfAttempts = (user) => {
  return user?.identityVerification?.attempts >= MAX_IDENTITY_VERIFICATION_ATTEMPTS
}

export const getUserPlatform = (initialPlatform = USER_PLATFORM.ANDROID) => {
  let platform = initialPlatform

  if (/android/i.test(navigator.userAgent)) {
    platform = USER_PLATFORM.ANDROID
  } else if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
    platform = USER_PLATFORM.IOS
  }

  return platform
}

export const isUserFromMobileDevice = () => {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0 || window.ReactNativeWebView
}

export const getUserTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

export const formatDate = (date, format) => {
  date = date._seconds ? new Date(date._seconds * 1000) : new Date(date)

  return date.toLocaleDateString(
    ...(format || [
      'en-US',
      {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      },
    ])
  )
}

export const isLinkActive = (route) => location.pathname.startsWith(route)

export const generateId = (length) => {
  let result = ''
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  const charactersLength = characters.length
  let counter = 0
  while (counter < length) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength))
    counter += 1
  }
  return result
}

export const debounce = (fn, delay) => {
  let timerId
  return (...args) => {
    clearTimeout(timerId)
    timerId = setTimeout(() => fn(...args), delay)
  }
}

export const handleError = (err, customMessage = '') => {
  toast.error(customMessage || ERROR_TEXT.default)
  console.error(err)
}

export const getRegistrationTitle = (user) => {
  const firstName = sessionStorage.getItem('firstName') || user?.firstName || ''

  return `Welcome${firstName && ', '}${firstName}!`
}

export const handleConnectSecondGmail = (userId, link = 'auth/gmail', signupAttrParam) => {
  generateAuthLinkPost(link, ['email'], signupAttrParam, userId)
    .then((authLink) => {
      if (link === 'auth/gmail') {
        localStorage.removeItem('jwtToken')
      }
      window.location.href = authLink
    })
    .catch(handleError)
}

export const selectReferralRewardTitle = (reward) => {
  const titles = {
    SIGN_UP_REFERRER: `${reward.connectedUserName} created an account`,
    SIGN_UP_REFERRED: `You were referred by ${reward.connectedUserName}`,
    CLAIM_REWARD_REFERRER: `${reward.connectedUserName} made a purchase`,
    CLAIM_REWARD_REFERRED: `Bonus referral reward with purchase`,
  }
  return titles[reward.reason]
}

export const truncateString = (str, maxLength) => {
  if (str.length > maxLength) {
    return str.slice(0, maxLength) + '...'
  }
  return str
}

export const objectEncode = (object) => {
  const str = []
  Object.keys(object).forEach((index) => {
    str.push(`${encodeURIComponent(index)}=${encodeURIComponent(object[index])}`)
  })
  return str.join('&')
}

export const getPlatform = () => {
  const userAgent = window.navigator.userAgent,
    platform = window.navigator?.userAgentData?.platform || window.navigator.platform,
    iosPlatforms = ['iPhone', 'iPad', 'iPod']

  if (iosPlatforms.indexOf(platform) !== -1) {
    return USER_PLATFORM.IOS
  }

  if (/Android/.test(userAgent)) {
    return USER_PLATFORM.ANDROID
  }

  return 'unknown'
}

export const initializeTagManager = (userId = null) => {
  try {
    TagManager.initialize({
      gtmId: process.env.REACT_APP_GOOGLE_TAG_ID,
      ...(userId && {
        dataLayer: {
          ...ANALYTICS_FRONTEND_EVENT.SET_USER_ID,
          user_id: userId,
        },
      }),
    })
  } catch (err) {
    captureEvent({
      message: ERRORS.GTM_INIT,
      level: 'error',
      extra: { err: err.message },
    })
  }
}

export const sendGA4Event = (eventData, params = {}, token) => {
  if (!eventData.eventParams) {
    eventData.eventParams = {}
  }

  Object.assign(eventData.eventParams, params)

  sendEventAnalytic(eventData, token)
}

export const sendRNMessage = (message) => {
  try {
    window.ReactNativeWebView?.postMessage(JSON.stringify(message))
  } catch (err) {
    captureEvent({
      message: ERRORS.REACT_NATIVE_POST_MESSAGE,
      level: 'error',
      extra: { err: err.message, data: message },
    })
  }
}

export const createImagesArray = (
  count,
  set,
  src,
  headerDiv,
  div,
  duration = 1,
  virtualCurrencyType,
  isSingleVirtualCurrencyClaim = false,
  alignRight = false
) => {
  const array = []

  for (let i = 0; i < count; i++) {
    array.push(
      <MovingImage
        key={i + src}
        div={div}
        src={src}
        index={i}
        headerDiv={headerDiv}
        animationDuration={duration}
        virtualCurrencyType={virtualCurrencyType}
        isSingleVirtualCurrencyClaim={isSingleVirtualCurrencyClaim}
        alignRight={alignRight}
      />
    )
  }

  set((prev) => [...prev, ...array])
}

export const createVirtualCurrencyImagesArrays = (
  sourceDiv,
  earnedCoins = 0,
  earnedPiggy = 0,
  setCoinsArray,
  setPigsArray,
  duration,
  animationLevel,
  alignRight = false
) => {
  const animationIconsCap = getAnimationIconsCap(animationLevel)
  const isSingleVirtualCurrencyClaim = earnedCoins === 0 || earnedPiggy === 0

  if (earnedCoins > 0 && setCoinsArray) {
    createImagesArray(
      Math.min(animationIconsCap, earnedCoins),
      setCoinsArray,
      '/images/coin.png',
      getHeaderImageDiv(false),
      sourceDiv,
      duration,
      VIRTUAL_CURRENCY_TYPE.COIN,
      isSingleVirtualCurrencyClaim,
      alignRight
    )
  }

  if (earnedPiggy > 0 && setPigsArray) {
    createImagesArray(
      Math.min(animationIconsCap, earnedPiggy),
      setPigsArray,
      '/images/pig.png',
      getHeaderImageDiv(true),
      sourceDiv,
      duration,
      VIRTUAL_CURRENCY_TYPE.PIGGY_BANK,
      isSingleVirtualCurrencyClaim,
      alignRight
    )
  }
}

export const getHeaderImageDiv = (isPig) =>
  document.getElementsByClassName(`gamesHeaderInput ${isPig ? 'pig' : 'coin'}`)[0]

export const sumFloat = (a, b) => Number(((a * 100 + b * 100) / 100).toFixed(2))

export const isRouteAvailable = (routes, pathname = location.pathname) => {
  const pathItems = pathname.split('/')

  return routes.some((route) => {
    const routeItems = route.path.split('/')

    if (routeItems.length === pathItems.length) {
      let exists = true

      routeItems.map((text, key) => {
        exists = exists && (text.startsWith(':') || text === pathItems[key])
      })

      return exists
    }

    return false
  })
}

export const checkDateIsLessToday = (date) => date._seconds <= new Date().getTime() / 1000

export const checkVisible = (elm) => {
  const rect = elm.getBoundingClientRect()
  const viewHeight = Math.max(document.documentElement.clientHeight, window.innerHeight)
  return !(rect.bottom - 60 < 0 || rect.top - viewHeight + 70 >= 0)
}

export const handleStripeIdentity = (userId, navigate) => {
  createVerificationSession()
    .then(async (session) => {
      const stripe = await loadStripe(`${process.env.REACT_APP_STRIPE_PUBLIC_API_KEY}`)
      if (!stripe) {
        throw new Error('Stripe failed to load')
      }

      const data = await stripe.verifyIdentity(session.clientSecret)
      if (!data.error) {
        navigate('/verification/result')
      }

      sendGA4Event(ANALYTICS_EVENT.IDENTITY_VERIFICATION_FINISH, {
        session_id: session.sessionId,
      })
    })
    .catch((err) => {
      handleError(err, err.response?.data?.message ?? null)
      captureEvent({
        message: ERRORS.STRIPE_IDENTITY,
        level: 'error',
        extra: { err, userId },
      })
    })
}

export const handleVerisoulIdentity = (userId) => {
  createVerisoulSession({ userId })
    .then(({ sessionId }) => {
      const verisoulAppUrl = process.env.REACT_APP_VERISOUL_APP_URL
      const redirectUrl = new URL('/verification/result', window.location.origin)
      redirectUrl.searchParams.set('provider', 'verisoul')
      redirectUrl.searchParams.set('session_id', sessionId)

      const verificationUrl = new URL(verisoulAppUrl)
      verificationUrl.searchParams.set('session_id', sessionId)
      verificationUrl.searchParams.set('redirect_url', redirectUrl.toString())

      window.location.href = verificationUrl.toString()
    })
    .catch((err) => {
      handleError(err, err.response?.data?.message ?? null)
      captureEvent({
        message: ERRORS.IDENTITY_VERIFICATION,
        level: 'error',
        extra: { err, userId },
      })
    })
}

export const openGameLink = (clickableLink, user, gameDetails) => {
  if (!window.ReactNativeWebView) {
    // On web and Apple devices, window.open often doesn't work.
    // In such cases, use window.location.href instead.
    const isAppleDevice =
      (/iPad|iPhone|iPod|Macintosh|MacIntel|MacPPC|Mac68K|AppleTV/.test(navigator.platform) ||
        (/Mac/.test(navigator.userAgent) && navigator.maxTouchPoints > 0)) &&
      !window.MSStream
    isAppleDevice ? (window.location.href = clickableLink) : window.open(clickableLink)
  } else {
    sendRNMessage({
      status: REACT_NATIVE_STATUS.TRY_GAME,
      userId: user.id,
      id: gameDetails.id,
      url: clickableLink,
      unifiedAppId: gameDetails.unified_app_id,
    })
  }
}

/**
 * Handler for axios errors
 * Depending on the code, message, or parameters of the error, it performs different functions and throws the error that was received
 *
 * @function
 * @returns {void}
 */
// Ideally this refresh would be in a react hook but our structure is non-standard so instead I store a refrence to the function to prevent multiple invalid requests from all refreshing
let refreshingFunc = undefined
export const errorInterceptorHandler = async (error) => {
  const code = _.get(error, 'response.data.code')
  const message = _.get(error, 'response.data.message')
  const additionalParams = _.get(error, 'response.data.additionalParams')
  const refresh = useRefreshToken()

  const prevRequest = error?.config

  if (error?.response?.status === 403 && !prevRequest.sent) {
    try {
      prevRequest.sent = true
      if (!refreshingFunc) {
        refreshingFunc = refresh()
      }
      const newAccessToken = await refreshingFunc
      prevRequest.headers.Authorization = `Bearer ${newAccessToken}`
      localStorage.setItem(LOCAL_STORAGE.JWT_TOKEN, newAccessToken)
      return axiosBackendInstance(prevRequest).catch((err) => {
        captureEvent({
          message: ERRORS.REFRESH_REREQ_ERROR,
          level: 'error',
          extra: {
            err: err.message,
            token: localStorage.getItem(LOCAL_STORAGE.JWT_TOKEN),
          },
        })
        throw err
      })
    } catch (err) {
      localStorage.removeItem(LOCAL_STORAGE.JWT_TOKEN)
      return
    } finally {
      refreshingFunc = undefined
    }
  }

  if (ERROR_REGEX.account_blocked.test(message) && window.location.pathname !== '/blocked') {
    window.location.href = '/blocked'
  }

  if (ERROR_REGEX.user_not_found.test(message)) {
    localStorage.removeItem(LOCAL_STORAGE.JWT_TOKEN)
    sendRNMessage({ status: REACT_NATIVE_STATUS.LOGOUT_USER })
    window.location.href = '/auth'
  }

  if (code && errorHandler[code]) {
    await errorHandler[code](message, additionalParams)
  } else if (error.message.search('throttled') !== -1 && !window.ReactNativeWebView) {
    window.location.reload()
  }
  error.message = `${error.message} connection type: ${_.get(
    window,
    'navigator.connection.effectiveType',
    'not found'
  )}`
  throw error
}

export const getYoutubeVideoId = (url) => {
  const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/
  const match = url.match(regExp)
  return match && match[7].length == 11 && match[7]
}

export const fetchToBase64 = async (src, onSuccess, errorMessage, onError = null) => {
  return await fetch(src)
    .then((response) => response.blob())
    .then((blob) => {
      const reader = new FileReader()
      reader.readAsDataURL(blob)
      reader.onloadend = () => {
        const dataUrl = reader.result.toString()

        onSuccess(dataUrl)
      }
    })
    .catch((err) => {
      captureEvent({
        message: errorMessage,
        level: 'error',
        extra: { err: err.message },
      })

      onError && onError()
    })
}

/**
 * Play on day visibility check
 *
 * @param {object} day - day data
 * @param {string} timer - expiration timer string
 * @returns {boolean}
 */
export const checkDayVisible = (day, timer) => {
  return (
    !day.claimed &&
    (timer !== TIMER_EXPIRED || (timer === TIMER_EXPIRED && 'claimed' in day)) &&
    (!day.endDate || day.endDate?._seconds > new Date().getTime() / 1000 || 'claimed' in day)
  )
}

/**
 * Playtime visibility check
 *
 * @param {object} offer
 * @param {object} playtime
 * @param {number} index
 * @returns {boolean}
 */
export const checkPlaytimeVisible = (offer, playtime, index) => {
  if (
    offer.expirationTimestamp &&
    new Date().getTime() / 1000 >= offer.expirationTimestamp._seconds
  ) {
    return playtime.completed && !playtime.claimed
  } else {
    if (
      offer.maxPlaytimeTasksAvailable &&
      offer.maxPlaytimeTasksAvailable !== -1 &&
      index >= offer.maxPlaytimeTasksAvailable
    ) {
      return false
    }

    return !playtime.claimed
  }
}

/**
 * Returns start date of the play on day task
 *
 * @param {object} day - play on day object
 * @param {boolean} isActivated - check if offer is activated
 * @param {boolean} validating - check if offer is validating
 * @returns {string | null}
 */
export const getDayExpirationTimestamp = (day, isActivated = true, validating = false) => {
  if (
    isActivated &&
    !validating &&
    day.startDate?._seconds >= new Date().getTime() / 1000 &&
    day.startDate?._seconds - new Date().getTime() / 1000 < 86400
  ) {
    return day.startDate
  }

  return null
}

/**
 * Function for checking whether the offer is validating
 *
 * @param {object} activatedOffer
 * @param {object} specialOffer
 * @returns {boolean}
 */
export const checkOfferValidating = (activatedOffer, specialOffer) => {
  //Real install attribution logic
  if (activatedOffer?.mmpAttributionRequiredForRewards) {
    return !activatedOffer.isInstallAttributed
  }

  //Fake validation is not needed for playtime-rewarded offers or non-UA offers
  if (specialOffer.playtimeRewards || !specialOffer.isUAOffer) {
    return false
  }

  //Fake validation logic (timer)
  const fakeValidationTime =
    (+process.env.REACT_APP_OFFER_INSTALL_VALIDATING_TIME ||
      INSTALL_VALIDATING_MINUTES * MILLISECONDS_IN_MINUTE) / 1000

  return (
    activatedOffer.activationTimeStamp._seconds + fakeValidationTime > new Date().getTime() / 1000
  )
}

export const showTOSPromptForUser = (user, lastTOSUpdate) => {
  if (window.location.pathname === '/logout') {
    return false
  }
  return !user?.acceptedTOS || user?.acceptedTOS?._seconds < lastTOSUpdate?._seconds
}

export const matchInChildren = (children, val) => {
  const regex = new RegExp(val, 'i')

  if (Array.isArray(children)) {
    return children.some((child) => typeof child === 'string' && regex.test(child))
  }

  return typeof children === 'string' && regex.test(children)
}

// export const refreshAuthToken = async (token) => {
//   if (isExpired(token) && !window.refreshTokenPromise) {
//     window.refreshTokenPromise = (async () => {
//       try {
//         const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/auth/refresh`, {
//           method: 'GET',
//           credentials: 'include',
//           headers: {
//             Authorization: `Bearer ${token}`,
//           },
//         })

//         if (!response.ok) {
//           throw new Error('Failed to refresh token')
//         }

//         const data = await response.json()
//         localStorage.setItem(LOCAL_STORAGE.JWT_TOKEN, data.accessToken)
//         return data.accessToken
//       } catch (err) {
//         localStorage.removeItem(LOCAL_STORAGE.JWT_TOKEN)
//         captureEvent({
//           message: ERRORS.LOGOUT_REFRESH,
//           level: 'error',
//           extra: { err: err.message, location: 'utils' },
//         })
//         return null
//       } finally {
//         window.refreshTokenPromise = null
//       }
//     })()
//   }

//   return window.refreshTokenPromise ? await window.refreshTokenPromise : token
// }

const reloadPage = () => {
  if (window.ReactNativeWebView) {
    sendRNMessage({
      status: REACT_NATIVE_STATUS.RELOAD_WEB_VIEW,
    })
  } else {
    window.location.reload()
  }
}

const hashManifest = (manifest) => {
  const str = JSON.stringify(manifest)
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) >>> 0 // Bitwise to ensure 32-bit integer
  }
  return hash.toString(16) // Convert to hex string
}

export const refreshBundle = async () => {
  const lastCheckTime = parseInt(localStorage.getItem(LOCAL_STORAGE.LAST_SCRIPT_CHECK) || '0', 10)
  const lastManifest = localStorage.getItem(LOCAL_STORAGE.MANIFEST)
  const now = Date.now()

  if (isNaN(lastCheckTime) || lastCheckTime === 0) {
    localStorage.setItem(LOCAL_STORAGE.LAST_SCRIPT_CHECK, String(now - CHECK_BUNDLE_TIME))
    return
  }

  if (lastCheckTime && now - lastCheckTime < CHECK_BUNDLE_TIME) {
    return
  }

  if (!window.scriptCheckPromise) {
    window.scriptCheckPromise = (async () => {
      try {
        const response = await fetch('/asset-manifest.json', {
          method: 'GET',
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            Pragma: 'no-cache',
            Expires: '0',
          },
        })

        if (!response.ok) {
          captureEvent({
            message: ERRORS.MANIFEST_CHECK_ERROR,
            level: 'error',
            extra: { message: 'Cannot fetch manifest data!' },
          })
          return
        }

        const manifest = await response.json()
        const manifestHash = hashManifest(manifest)

        if (!lastManifest) {
          localStorage.setItem(LOCAL_STORAGE.MANIFEST, manifestHash)
        } else if (lastManifest !== manifestHash) {
          localStorage.setItem(LOCAL_STORAGE.MANIFEST, manifestHash)
          reloadPage()
        }
      } catch (err) {
        captureEvent({
          message: ERRORS.MANIFEST_CHECK_ERROR,
          level: 'error',
          extra: { err },
        })
      } finally {
        localStorage.setItem(LOCAL_STORAGE.LAST_SCRIPT_CHECK, String(now))
        window.scriptCheckPromise = null
      }
    })()
  }

  return window.scriptCheckPromise
}
